import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AskRequest } from '@hone-automation/common';
import { prefixClass } from 'lib/utils';
import { usePatchQueueRequestMutation } from 'hooks/queue';
import { IconValidateNew, IconCancel } from 'components/Icons';
import IconButton from 'presentation/components/KSQ2/Common/IconButton';
import './EditableTitle.scss';

interface EditableTitleProps {
  request: AskRequest;
  canEdit?: boolean;
}

type EditingState = 'display' | 'editing' | 'saving';

/**
 * EditableTitle - Click-to-edit title component for request headers
 *
 * - Displays title (or falls back to identifier)
 * - Click to edit with save/cancel buttons
 * - Patches title via API
 */
export const EditableTitle: React.FC<EditableTitleProps> = ({ request, canEdit = true }) => {
  const prefix = prefixClass('editable-title');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { locationId } = useParams<{ locationId: string }>();

  const [state, setState] = useState<EditingState>('display');
  const [tempValue, setTempValue] = useState('');

  const patchMutation = usePatchQueueRequestMutation();

  // Reset tempValue when switching requests
  useEffect(() => setTempValue(''), [request.id]);
  const displayValue = tempValue || request.title || request.identifier || `Request #${request.id.slice(0, 8)}`;

  const handleEditClick = () => {
    setTempValue(displayValue);
    setState('editing');
  };

  const handleSaveClick = async () => {
    if (tempValue.trim() === '') return;

    setState('saving');
    const nextTitle = tempValue.trim();
    try {
      await patchMutation.mutateAsync({
        id: request.id,
        title: nextTitle,
        locationId,
      });
      setTempValue(nextTitle);
      setState('display');
    } catch (error) {
      console.error('Failed to update title:', error);
      setState('editing');
    }
  };

  const handleCancelClick = () => {
    setTempValue('');
    setState('display');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveClick();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelClick();
    }
  };

  // Auto-focus input when entering edit mode
  useEffect(() => {
    if (state === 'editing' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [state]);

  // Click outside to cancel editing
  useEffect(() => {
    if (state !== 'editing') return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleCancelClick();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [state]);

  return (
    <div
      ref={containerRef}
      className={`${prefix()} ${prefix(`state-${state}`)} ${canEdit ? prefix('--editable') : ''}`}
    >
      {state === 'display' && (
        <div className={prefix('__display')}>
          {canEdit ? (
            <button className={prefix('__text')} onClick={handleEditClick} type="button" title="Click to edit title">
              {displayValue}
            </button>
          ) : (
            <span className={prefix('__text')}>{displayValue}</span>
          )}
        </div>
      )}

      {(state === 'editing' || state === 'saving') && (
        <div className={prefix('__editing')}>
          <input
            ref={inputRef}
            type="text"
            className={prefix('__input')}
            value={tempValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={state === 'saving'}
            placeholder="Enter title..."
          />
          <div className={prefix('__actions')}>
            <IconButton
              icon={<IconValidateNew />}
              onClick={handleSaveClick}
              disabled={state === 'saving'}
              tooltip="Save (Enter)"
              size="small"
              rounded
            />
            <IconButton
              icon={<IconCancel />}
              onClick={handleCancelClick}
              disabled={state === 'saving'}
              tooltip="Cancel (Esc)"
              size="small"
              rounded
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EditableTitle;
